// Admin newsletter (marketing broadcast) API client. All endpoints require an
// admin bearer token; callers pass the token resolved from useAuth().getToken().

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export interface NewsletterActivity {
  id: string;
  /** 'course' items are recorded courses (no session/venue). Defaults to 'activity'. */
  type?: 'activity' | 'course';
  slug: string;
  titleCN: string | null;
  titleEN: string | null;
  location?: string | null;
  locationDetails?: string | null;
  region?: string[];
  ageMin: number;
  ageMax: number;
  duration: number;
  description: string;
  category?: string | null;
  coverImageUrl: string | null;
  hostName: string;
  nextSession: { startTime: string; endTime: string } | null;
}

export interface Segment {
  id: string;
  name: string;
}

export interface CampaignContent {
  hero: {
    showImage: boolean;
    showTitle: boolean;
    showWelcome: boolean;
    title: string;
    dateRange: string;
    imageUrl: string;
  };
  welcome?: { heading: string; body: string };
  note?: string;
  activities: { activityId: string; blurb?: string }[];
}

export type CampaignStatus = 'DRAFT' | 'SCHEDULED' | 'SENDING' | 'SENT' | 'FAILED' | 'CANCELLED';

export interface Campaign {
  id: string;
  subject: string;
  previewText: string | null;
  segmentId: string | null;
  scheduledAt: string | null;
  status: CampaignStatus;
  content: CampaignContent;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
}

export interface CampaignInput {
  subject: string;
  previewText?: string;
  segmentId?: string;
  content: CampaignContent;
}

async function request<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body?.success === false) {
    throw new Error(body?.error || `Request failed (${res.status})`);
  }
  return body.data as T;
}

export const newsletterApi = {
  getActivities: (token: string) =>
    request<NewsletterActivity[]>(token, '/admin/newsletter/activities'),

  getSegments: (token: string) => request<Segment[]>(token, '/admin/newsletter/segments'),

  listCampaigns: (token: string) => request<Campaign[]>(token, '/admin/newsletter/campaigns'),

  getCampaign: (token: string, id: string) =>
    request<Campaign>(token, `/admin/newsletter/campaigns/${id}`),

  createCampaign: (token: string, input: CampaignInput) =>
    request<Campaign>(token, '/admin/newsletter/campaigns', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateCampaign: (token: string, id: string, input: CampaignInput) =>
    request<Campaign>(token, `/admin/newsletter/campaigns/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  cloneCampaign: (token: string, id: string) =>
    request<Campaign>(token, `/admin/newsletter/campaigns/${id}/clone`, { method: 'POST' }),

  preview: (token: string, id: string) =>
    request<{ html: string }>(token, `/admin/newsletter/campaigns/${id}/preview`, {
      method: 'POST',
    }),

  test: (token: string, id: string, recipients: string[]) =>
    request<unknown>(token, `/admin/newsletter/campaigns/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    }),

  send: (token: string, id: string, scheduledAt?: string | null) =>
    request<{ campaignId: string; broadcastId: string; status: 'SCHEDULED' | 'SENT' }>(
      token,
      `/admin/newsletter/campaigns/${id}/send`,
      { method: 'POST', body: JSON.stringify({ scheduledAt: scheduledAt ?? null }) }
    ),

  cancel: (token: string, id: string) =>
    request<Campaign>(token, `/admin/newsletter/campaigns/${id}/cancel`, { method: 'POST' }),
};
